import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

export const RECAPTCHA_HEADER_NAME = 'X-Recaptcha-Token';

interface RecaptchaActionRule {
    action: string;
    method: string;
    pattern: RegExp;
}

const RECAPTCHA_ERROR_TOKENS = ['captcha', 'recaptcha', 're-captcha', 'score'];

const RECAPTCHA_ACTION_RULES: RecaptchaActionRule[] = [
    {
        action: 'auth_login',
        method: 'POST',
        pattern: /^\/v1\/token$/
    },
    {
        action: 'person_lookup',
        method: 'GET',
        pattern: /^\/v1\/person\/[^/]+\/[^/]+$/
    },
    {
        action: 'cart_utm',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/utm$/
    },
    {
        action: 'cart_store',
        method: 'POST',
        pattern: /^\/v1\/cart$/
    },
    {
        action: 'cart_origin_update',
        method: 'PUT',
        pattern: /^\/v1\/cart\/[^/]+\/origin$/
    },
    {
        action: 'cart_who_pays_update',
        method: 'PUT',
        pattern: /^\/v1\/cart\/[^/]+\/who-pays$/
    },
    {
        action: 'cart_update',
        method: 'PATCH',
        pattern: /^\/v1\/cart\/[^/]+$/
    },
    {
        action: 'cart_show',
        method: 'GET',
        pattern: /^\/v1\/cart\/[^/]+$/
    },
    {
        action: 'cart_destroy',
        method: 'DELETE',
        pattern: /^\/v1\/cart\/[^/]+$/
    },
    {
        action: 'cart_item_store',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/item$/
    },
    {
        action: 'cart_item_show',
        method: 'GET',
        pattern: /^\/v1\/cart\/[^/]+\/item\/[^/]+$/
    },
    {
        action: 'cart_item_update',
        method: 'PATCH',
        pattern: /^\/v1\/cart\/[^/]+\/item\/[^/]+$/
    },
    {
        action: 'cart_item_clone',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/item\/[^/]+\/clone$/
    },
    {
        action: 'cart_massive_upload_file',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/items\/massive$/
    },
    {
        action: 'cart_item_destroy',
        method: 'DELETE',
        pattern: /^\/v1\/cart\/[^/]+\/item\/[^/]+$/
    },

    {
        action: 'cart_massive_status',
        method: 'DELETE',
        pattern: /^\/v1\/cart\/[^/]+\/item\/[^/]+$/
    },
    {
        action: 'geo_autocomplete',
        method: 'GET',
        pattern: /^\/v1\/geo\/autocomplete$/
    },
    {
        action: 'geo_address',
        method: 'GET',
        pattern: /^\/v1\/geo\/address$/
    },
    {
        action: 'geo_reverse',
        method: 'GET',
        pattern: /^\/v1\/geo\/reverse$/
    },
    {
        action: 'price_show',
        method: 'GET',
        pattern: /^\/v1\/price\/[^/]+$/
    },
    {
        action: 'shipping_headquarters_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/headquarters$/
    },
    {
        action: 'shipping_destinations_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/destinations$/
    },
    {
        action: 'shipping_return_charge_destinations_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/return-charge-destinations$/
    },
    {
        action: 'shipping_ubigeos_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/ubigeos$/
    },
    {
        action: 'shipping_stores_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/stores$/
    },
    {
        action: 'shipping_article_categories_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/article-categories$/
    },
    {
        action: 'shipping_standard_sizes_index',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/standard-sizes$/
    },
    {
        action: 'shipping_calculator',
        method: 'POST',
        pattern: /^\/v1\/shipping-records\/calculator$/
    },
    {
        action: 'shipping_coupon_store',
        method: 'POST',
        pattern: /^\/v1\/shipping-records\/cart\/[^/]+\/coupon$/
    },
    {
        action: 'shipping_pin_update',
        method: 'PUT',
        pattern: /^\/v1\/shipping-records\/cart\/[^/]+\/pin$/
    },
    {
        action: 'cart_massive_template',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/cart\/[^/]+\/massive-template$/
    },
    {
        action: 'cart_massive_status',
        method: 'GET',
        pattern: /^\/v1\/cart\/[^/]+\/items\/massive\/status\/[^/]+$/
    },
    {
        action: 'shipping_label_show',
        method: 'GET',
        pattern: /^\/v1\/shipping-records\/cart\/[^/]+\/label$/
    },
    {
        action: 'payment_niubiz_session',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/payment\/niubiz\/session$/
    },
    {
        action: 'payment_pagoefectivo',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/payment\/pagoefectivo$/
    },
    {
        action: 'payment_tienda',
        method: 'POST',
        pattern: /^\/v1\/cart\/[^/]+\/payment\/tienda$/
    },
    {
        action: 'presale_generate',
        method: 'POST',
        pattern: /^\/v1\/presale\/generate$/
    }
];

export function normalizeUrlBase(url: string): string {
    while (url.endsWith('/')) {
        url = url.slice(0, -1);
    }

    return url;
}

export function getRequestPath(url: string): string {
    try {
        const baseUrl = typeof globalThis.location?.origin === 'string' ? globalThis.location.origin : 'http://localhost';
        return new URL(url, baseUrl).pathname.replaceAll(/\/{2,}/g, '/');
    } catch {
        return url.replace(/^[a-zA-Z]+:\/\/[^/]+/, '').replaceAll(/\/{2,}/g, '/');
    }
}

export function isShippingApiRequest(request: HttpRequest<unknown>, apiBase: string): boolean {
    const normalizedApiBase = normalizeUrlBase(apiBase);
    return normalizeUrlBase(request.url).startsWith(normalizedApiBase);
}

export function isTokenRefreshRequest(request: HttpRequest<unknown>): boolean {
    return request.method === 'POST' && getRequestPath(request.url) === '/v1/token-refresh';
}

export function resolveRecaptchaAction(request: HttpRequest<unknown>, apiBase: string): string | null {
    if (!isShippingApiRequest(request, apiBase)) {
        return null;
    }

    if (isTokenRefreshRequest(request)) {
        return null;
    }

    if (request.method === 'POST' && getRequestPath(request.url) === '/v1/token') {
        return 'auth_login';
    }

    const path = getRequestPath(request.url);

    for (const rule of RECAPTCHA_ACTION_RULES) {
        if (request.method === rule.method && rule.pattern.test(path)) {
            return rule.action;
        }
    }

    return null;
}

function extractErrorText(payload: unknown, visited = new Set<unknown>()): string {
    if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean') {
        return String(payload);
    }

    if (!payload || typeof payload !== 'object' || visited.has(payload)) {
        return '';
    }

    visited.add(payload);

    if (Array.isArray(payload)) {
        return payload.map((value) => extractErrorText(value, visited)).join(' ');
    }

    return Object.values(payload as Record<string, unknown>)
        .map((value) => extractErrorText(value, visited))
        .join(' ');
}

export function isRecaptchaRetryableError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
        return false;
    }

    const message = `${error.message} ${extractErrorText(error.error)}`.toLowerCase();
    return RECAPTCHA_ERROR_TOKENS.some((token) => message.includes(token));
}
